import type {
  CprCenterlinePathPoint,
  CprOverlayGeometry,
  CprOverlayIndexBand,
  CprOverlayPerpendicularLine,
  CprOverlayPoint,
} from '../cprImageResources';
import type { PerpendicularLine } from 'utils/CPRUtilities/markerLineTypes';

export type CprIndexBandOverlay = {
  polygonPoints: string;
  boundaryLines: PerpendicularLine[];
  source: 'server' | 'path';
  coordinateSpace: 'image' | 'viewport';
};

function minIndexDistance(
  point: CprCenterlinePathPoint,
  pathArrayIndex: number,
  targetIndex: number,
) {
  const centerlineCandidates = [point.index, point.sourceCenterlineIndex].filter(
    (value): value is number => Number.isFinite(value),
  );

  // front_data mapping rows are not guaranteed to have a 1:1 relation between
  // path array offset and source centerline index. Prefer the real centerline
  // index whenever it exists; otherwise a marker can snap to the row number and
  // jump when the CPR angle or branch geometry changes.
  const candidates = centerlineCandidates.length
    ? centerlineCandidates
    : Number.isFinite(pathArrayIndex)
      ? [pathArrayIndex]
      : [];

  if (!candidates.length) return Number.POSITIVE_INFINITY;
  return Math.min(
    ...candidates.map((candidate) => Math.abs(candidate - targetIndex)),
  );
}

export function nearestCprPathArrayIndex(
  path: CprCenterlinePathPoint[],
  centerlineIndex: number,
) {
  if (!path.length) return -1;
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  path.forEach((point, index) => {
    const distance = minIndexDistance(point, index, centerlineIndex);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

export function projectCprCenterlinePathToViewport(
  path: CprCenterlinePathPoint[],
  imageToViewportPoint: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  },
): CprCenterlinePathPoint[] {
  return path.map((point) => {
    const projected = imageToViewportPoint({ x: point.x, y: point.y });
    return {
      ...point,
      x: projected.x,
      y: projected.y,
    };
  });
}

export function buildCprPerpendicularLines(
  path: CprCenterlinePathPoint[],
  bandWidth: number,
): PerpendicularLine[] {
  return path.map((base, index) => {
    const prev = path[Math.max(0, index - 1)] ?? base;
    const next = path[Math.min(path.length - 1, index + 1)] ?? base;
    const perpY = next.x - prev.x;
    const perpX = -(next.y - prev.y);
    const norm = Math.hypot(perpX, perpY) || 1;
    const unitX = (perpX / norm) * bandWidth;
    const unitY = (perpY / norm) * bandWidth;

    return {
      startX: base.x - unitX,
      startY: base.y - unitY,
      endX: base.x + unitX,
      endY: base.y + unitY,
    };
  });
}

function isFinitePoint(
  point: CprOverlayPoint | undefined,
): point is CprOverlayPoint {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

function toPerpendicularLine(
  line: CprOverlayPerpendicularLine | undefined,
): PerpendicularLine | null {
  if (!line || !isFinitePoint(line.start) || !isFinitePoint(line.end)) {
    return null;
  }
  return {
    startX: line.start.x,
    startY: line.start.y,
    endX: line.end.x,
    endY: line.end.y,
  };
}

function pointsToSvgPolygon(points: CprOverlayPoint[] | undefined) {
  if (!points?.length) return '';
  const safePoints = points.filter(isFinitePoint);
  if (safePoints.length < 3) return '';
  return safePoints.map((point) => `${point.x},${point.y}`).join(' ');
}

function normalizeIndexRange(indexRange: [number, number]) {
  const start = Math.min(indexRange[0], indexRange[1]);
  const end = Math.max(indexRange[0], indexRange[1]);
  return [start, end] as [number, number];
}

function buildLegacyHorizontalBand({
  centerlinePath,
  indexRange,
  width,
}: {
  centerlinePath: CprCenterlinePathPoint[];
  indexRange: [number, number];
  width: number;
}): CprIndexBandOverlay | null {
  const startArrayIndex = nearestCprPathArrayIndex(
    centerlinePath,
    indexRange[0],
  );
  const endArrayIndex = nearestCprPathArrayIndex(centerlinePath, indexRange[1]);
  if (startArrayIndex < 0 || endArrayIndex < 0) return null;

  const startY = centerlinePath[startArrayIndex]?.y;
  const endY = centerlinePath[endArrayIndex]?.y;
  if (!Number.isFinite(startY) || !Number.isFinite(endY)) return null;

  const safeWidth = Math.max(1, width);
  const y0 = Math.min(startY, endY);
  const y1 = Math.max(startY, endY);
  const startLine = {
    startX: 0,
    startY: y0,
    endX: safeWidth,
    endY: y0,
  };
  const endLine = {
    startX: 0,
    startY: y1,
    endX: safeWidth,
    endY: y1,
  };

  return {
    polygonPoints: `0,${y0} 0,${y1} ${safeWidth},${y1} ${safeWidth},${y0}`,
    boundaryLines: [startLine, endLine],
    source: 'path',
    coordinateSpace: 'image',
  };
}

function findServerIndexBand(
  overlayGeometry: CprOverlayGeometry | undefined,
  key: string | undefined,
  indexRange: [number, number],
): CprOverlayIndexBand | null {
  const bands = overlayGeometry?.indexBands ?? [];
  if (!bands.length) return null;
  const [start, end] = normalizeIndexRange(indexRange);

  const matchRange = (band: CprOverlayIndexBand) => {
      const bandStart = Math.min(band.indexStart, band.indexEnd);
      const bandEnd = Math.max(band.indexStart, band.indexEnd);
      return bandStart === start && bandEnd === end;
  };

  return (
    bands.find((band) => key && band.key === key && matchRange(band)) ??
    bands.find(matchRange) ??
    null
  );
}

export function buildCprOverlayPerpendicularLines({
  centerlinePath,
  overlayGeometry,
  fallbackBandWidth,
}: {
  centerlinePath: CprCenterlinePathPoint[];
  overlayGeometry?: CprOverlayGeometry;
  fallbackBandWidth: number;
}): {
  lines: PerpendicularLine[];
  source: 'server' | 'path';
} {
  const overlayLines = overlayGeometry?.perpendicularLines ?? [];
  if (!overlayLines.length || !centerlinePath.length) {
    return {
      lines: buildCprPerpendicularLines(centerlinePath, fallbackBandWidth),
      source: 'path',
    };
  }

  const fallbackLines = buildCprPerpendicularLines(
    centerlinePath,
    fallbackBandWidth,
  );

  if (overlayLines.length === centerlinePath.length) {
    const orderedLines = overlayLines.map((line, index) =>
      toPerpendicularLine(line) ?? fallbackLines[index],
    );
    return {
      lines: orderedLines,
      source: 'server',
    };
  }

  const serverLineByIndex = new Map<number, PerpendicularLine>();
  overlayLines.forEach((line) => {
    const normalizedLine = toPerpendicularLine(line);
    if (!normalizedLine || !Number.isFinite(line.index)) return;
    serverLineByIndex.set(line.index, normalizedLine);
  });

  if (!serverLineByIndex.size) {
    return {
      lines: fallbackLines,
      source: 'path',
    };
  }

  return {
    lines: centerlinePath.map(
      (point, index) =>
        serverLineByIndex.get(point.index) ?? fallbackLines[index],
    ),
    source: 'server',
  };
}

export function buildCprIndexBandOverlay({
  centerlinePath,
  indexRange,
  width,
  perpendicularLines,
  overlayGeometry,
  bandKey,
}: {
  centerlinePath: CprCenterlinePathPoint[];
  indexRange: [number, number];
  width: number;
  perpendicularLines?: PerpendicularLine[];
  overlayGeometry?: CprOverlayGeometry;
  bandKey?: string;
}): CprIndexBandOverlay | null {
  void perpendicularLines;
  if (!centerlinePath.length) return null;
  const serverBand = findServerIndexBand(overlayGeometry, bandKey, indexRange);
  if (serverBand) {
    const polygonPoints = pointsToSvgPolygon(serverBand.polygon);
    const boundaryLines = serverBand.boundaryLines
      ?.map(toPerpendicularLine)
      .filter((line): line is PerpendicularLine => !!line);
    if (polygonPoints) {
      const fallbackStart = nearestCprPathArrayIndex(
        centerlinePath,
        indexRange[0],
      );
      const fallbackEnd = nearestCprPathArrayIndex(
        centerlinePath,
        indexRange[1],
      );
      const fallbackBoundaryLines = [
        perpendicularLines?.[fallbackStart],
        perpendicularLines?.[fallbackEnd],
      ].filter((line): line is PerpendicularLine => !!line);
      return {
        polygonPoints,
        boundaryLines: boundaryLines?.length
          ? boundaryLines
          : fallbackBoundaryLines,
        source: 'server',
        coordinateSpace: 'image',
      };
    }
  }

  return buildLegacyHorizontalBand({
    centerlinePath,
    indexRange,
    width,
  });
}
